import React, { ReactNode, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ...props
}: HeaderProps): ReactNode {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  // Check if device is mobile
  const checkIsMobile = () => {
    const userAgent = navigator.userAgent;
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    );
    const isSmallScreen = window.innerWidth <= 768;
    setIsMobile(isMobileDevice || isSmallScreen);
  };

  // Check scroll state for mobile carousel
  const checkScrollState = () => {
    if (scrollContainerRef.current && isMobile) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Handle scroll for mobile carousel
  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const newScrollLeft =
        direction === 'left'
          ? scrollContainerRef.current.scrollLeft - scrollAmount
          : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  // Touch handling for swipe gestures
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; time: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    const touch = e.targetTouches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    });
    setIsDragging(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !touchStart) return;

    const touch = e.targetTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStart.x);
    const deltaY = Math.abs(touch.clientY - touchStart.y);

    // If horizontal movement is greater than vertical, it's a scroll gesture
    if (deltaX > deltaY && deltaX > 10) {
      setIsDragging(true);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile || !touchStart) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = Math.abs(touch.clientY - touchStart.y);
    const timeDelta = Date.now() - touchStart.time;

    // Only process as swipe if:
    // 1. Horizontal movement is significant (> 50px)
    // 2. Vertical movement is minimal (< 30px)
    // 3. Was actually dragging
    // 4. Duration was short enough (< 300ms for quick swipe)
    const isHorizontalSwipe = Math.abs(deltaX) > 50 && deltaY < 30 && isDragging;

    if (isHorizontalSwipe && timeDelta < 300) {
      if (deltaX < 0 && canScrollRight) {
        // Swipe left - scroll right
        handleScroll('right');
      } else if (deltaX > 0 && canScrollLeft) {
        // Swipe right - scroll left
        handleScroll('left');
      }
    }

    setTouchStart(null);
    setIsDragging(false);
  };

  useEffect(() => {
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      const container = scrollContainerRef.current;
      if (container) {
        checkScrollState();
        container.addEventListener('scroll', checkScrollState);
        window.addEventListener('resize', checkScrollState);

        return () => {
          container.removeEventListener('scroll', checkScrollState);
          window.removeEventListener('resize', checkScrollState);
        };
      }
    }
  }, [children, isMobile]);

  // Mobile Layout
  if (isMobile) {
    return (
      <IconPresentationProvider
        size="large"
        IconContainer={ToolButton}
      >
        <NavBar
          isSticky={isSticky}
          {...props}
        >
          <div className="flex h-auto min-h-[48px] w-full flex-col">
            <div className="border-primary-dark/20 flex h-12 w-full items-center justify-between border-b px-4">
              <div className="flex w-10 items-center">
                {isReturnEnabled && (
                  <Icons.ArrowLeft
                    className="text-primary h-5 w-5 cursor-pointer"
                    onClick={onClickReturn}
                  />
                )}
              </div>

              <div
                className={classNames(
                  'flex items-center',
                  isReturnEnabled && !isReturnEnabled && 'cursor-pointer'
                )}
                data-cy="return-to-work-list"
              >
                <div className="flex items-center">
                  {WhiteLabeling?.createLogoComponentFn?.(React, props) || <Icons.OHIFLogo />}
                </div>
              </div>

              <div className="flex w-10 items-center justify-end">
                <DropdownMenu
                  open={showMobileMenu}
                  onOpenChange={setShowMobileMenu}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-customblue-10 hover:bg-customblue-50 hover:text-info-primary h-10 w-10"
                    >
                      <Icons.ToolMoreMenu className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-80 max-w-[90vw]"
                    sideOffset={5}
                  >
                    {/* Patient Info Section */}
                    {PatientInfo && (
                      <div className="border-primary-dark/20 border-b p-2">
                        <div className="text-center">{PatientInfo}</div>
                      </div>
                    )}

                    {/* Menu Options */}
                    <div className="p-1">
                      {menuOptions.map((option, index) => {
                        return (
                          <DropdownMenuItem
                            key={index}
                            onSelect={option.onClick}
                            className="flex items-center gap-3 py-2 px-3"
                          >
                            {option.icon && (
                              <span className="text-customblue-10 flex h-4 w-4 items-center justify-center">
                                <Icons.ByName name={option.icon} />
                              </span>
                            )}
                            <span className="text-customblue-10 flex-1">{option.title}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Bottom Row - Tools Carousel - ISOLATED FROM PAGE SCROLL */}
            <div className="relative isolate h-12 w-full">
              {/* Carousel container - ISOLATED SCROLL */}
              <div className="relative h-full w-full">
                <div
                  ref={scrollContainerRef}
                  className="scrollbar-hide absolute inset-0 flex items-center overflow-x-auto overflow-y-hidden"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                    contain: 'layout style paint size',
                    isolation: 'isolate',
                    overscrollBehavior: 'contain',
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onScroll={e => {
                    e.stopPropagation();
                    checkScrollState();
                  }}
                >
                  <div
                    className="ohif-mobile-toolbar inline-flex h-full items-center gap-1 px-2"
                    style={{
                      minWidth: '100%',
                      height: '100%',
                    }}
                  >
                    {children}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </NavBar>
      </IconPresentationProvider>
    );
  }

  // Desktop Layout (Original)
  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className="relative h-[48px] items-center">
          <div className="absolute left-0 top-1/2 flex -translate-y-1/2 items-center">
            <div
              className={classNames(
                'mr-3 inline-flex items-center',
                isReturnEnabled && 'cursor-pointer'
              )}
              onClick={onClickReturn}
              data-cy="return-to-work-list"
            >
              {isReturnEnabled && <Icons.ArrowLeft className="text-primary ml-1 h-7 w-7" />}
              <div className="ml-1">
                {WhiteLabeling?.createLogoComponentFn?.(React, props) || <Icons.OHIFLogo />}
              </div>
            </div>
          </div>
          <div className="absolute top-1/2 left-[250px] h-8 -translate-y-1/2">{Secondary}</div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
            <div className="flex items-center justify-center space-x-2">{children}</div>
          </div>
          <div className="absolute right-0 top-1/2 flex -translate-y-1/2 select-none items-center">
            {UndoRedo}
            <div className="border-primary-dark mx-1.5 h-[25px] border-r"></div>
            {PatientInfo}
            <div className="border-primary-dark mx-1.5 h-[25px] border-r"></div>
            <div className="flex-shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-primary-dark mt-2 h-full w-full"
                  >
                    <Icons.GearSettings />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {menuOptions.map((option, index) => {
                    return (
                      <DropdownMenuItem
                        key={index}
                        onSelect={option.onClick}
                        className="flex items-center gap-2 py-2"
                      >
                        {option.icon && (
                          <span className="flex h-4 w-4 items-center justify-center">
                            <Icons.ByName name={option.icon} />
                          </span>
                        )}
                        <span className="flex-1">{option.title}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
